import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../../components/ui/dialog';
import { FileText, FileImage, File as FileIcon, Check, X, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const PENDIENTES = ['recibido', 'pendiente_login', 'pendiente_revision', 'requiere_mas_info', null, undefined];

const estadoVisual = (estado) => (estado === 'aprobado'
  ? { label: 'Aceptado', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' }
  : estado === 'rechazado'
    ? { label: 'Rechazado', cls: 'bg-red-100 text-red-700 border-red-300' }
    : { label: 'Pendiente', cls: 'bg-gray-100 text-gray-600 border-gray-300' });

const Icono = ({ type }) => {
  if ((type || '').startsWith('image/')) return <FileImage className="w-8 h-8 text-sky-600 shrink-0" />;
  if (type === 'application/pdf') return <FileText className="w-8 h-8 text-red-600 shrink-0" />;
  return <FileIcon className="w-8 h-8 text-gray-500 shrink-0" />;
};

export function CRMArchivoBurbuja({ m, base, headers, fecha, onRevisado }) {
  const meta = m.meta || {};
  const [accion, setAccion] = useState(null);
  const [comentario, setComentario] = useState('');
  const [busy, setBusy] = useState(false);
  const ev = estadoVisual(meta.estado_archivo);
  const pendiente = PENDIENTES.includes(meta.estado_archivo);

  const confirmar = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${base}/archivos/${encodeURIComponent(meta.filename)}/revision`,
        { accion, comentario }, { headers });
      const w = r.data.whatsapp || {};
      if (w.status === 'sent') toast.success(`Documento ${accion === 'aceptar' ? 'aceptado' : 'rechazado'} y aviso enviado`);
      else toast.warning('Estado actualizado, pero no se pudo enviar el aviso por WhatsApp');
      setAccion(null); setComentario('');
      onRevisado();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo actualizar'); }
    setBusy(false);
  };

  return (
    <div className="flex justify-start" data-testid={`crm-archivo-${meta.filename}`}>
      <div className="max-w-[80%] rounded-lg px-3 py-2 shadow-sm bg-white border">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Archivo enviado</p>
        <div className="flex items-center gap-2">
          <Icono type={meta.content_type} />
          <div className="min-w-0">
            {meta.url ? (
              <a href={meta.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-sky-700 hover:underline break-all inline-flex items-center gap-1"
                data-testid={`crm-archivo-link-${meta.filename}`}>{meta.filename}<ExternalLink className="w-3 h-3 shrink-0" /></a>
            ) : <span className="text-sm font-medium break-all">{meta.filename}</span>}
            <p className="text-[10px] text-gray-400">{fecha}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ev.cls}`} data-testid={`crm-archivo-estado-${meta.filename}`}>{ev.label}</span>
          {pendiente && (
            <>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => setAccion('aceptar')}
                data-testid={`crm-archivo-aceptar-${meta.filename}`}><Check className="w-3 h-3 mr-1" />Aceptar</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-300" onClick={() => setAccion('rechazar')}
                data-testid={`crm-archivo-rechazar-${meta.filename}`}><X className="w-3 h-3 mr-1" />Rechazar</Button>
            </>
          )}
        </div>
        {meta.nota_revision && <p className="text-xs text-gray-600 mt-1">Comentario: {meta.nota_revision}</p>}
        {meta.whatsapp_notificacion && meta.whatsapp_notificacion.status !== 'sent' && (
          <p className="text-xs text-amber-700 mt-1" title={meta.whatsapp_notificacion.error || meta.whatsapp_notificacion.motivo}
            data-testid={`crm-archivo-aviso-error-${meta.filename}`}>
            No se pudo enviar el aviso por WhatsApp (pasá el mouse para ver el detalle).
          </p>
        )}
      </div>
      <Dialog open={!!accion} onOpenChange={(o) => { if (!o) setAccion(null); }}>
        <DialogContent data-testid="crm-archivo-dialog">
          <DialogHeader>
            <DialogTitle>{accion === 'aceptar' ? 'Aceptar documento' : 'Rechazar documento'}</DialogTitle>
            <DialogDescription>
              {meta.filename}. Se le enviará un WhatsApp al matriculado. {accion === 'rechazar' ? 'Indicá el motivo (obligatorio).' : 'Podés agregar un comentario (opcional).'}
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={comentario} onChange={e => setComentario(e.target.value)}
            placeholder={accion === 'rechazar' ? 'Motivo del rechazo' : 'Comentario opcional'} data-testid="crm-archivo-comentario" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAccion(null)} data-testid="crm-archivo-cancelar">Cancelar</Button>
            <Button onClick={confirmar} disabled={busy || (accion === 'rechazar' && !comentario.trim())}
              className={accion === 'rechazar' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="crm-archivo-confirmar">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
